import {
  ShieldIcon,
  YearsIcon,
  PriceTagIcon,
  TrowelIcon,
  PhoneIcon,
  WaveIcon,
  TileIcon,
  CopingIcon,
  DeckIcon,
  RemodelIcon,
  GhostContractorIcon,
  CorporateIcon,
  HandshakeIcon,
  FamilyIcon,
  StarIcon,
  IconProps,
} from '../components/icons'

export type IconComponent = (props: IconProps) => JSX.Element

export const company = {
  name: 'A&J Pool Plaster, LLC',
  phoneDisplay: '(832) 920-0133',
  phoneHref: 'tel:+18329200133',
  email: 'AJPoolplaster@gmail.com',
  emailHref: 'mailto:AJPoolplaster@gmail.com',
  facebookUrl: 'https://www.facebook.com/Ajpoolplaster/',
  googleProfileUrl: 'https://share.google/vLSdGCc3rFFRozHxX',
  googleReviewUrl: 'https://share.google/vLSdGCc3rFFRozHxX',
  logo: 'https://cdn.magicpatterns.com/uploads/vf8SZ5gGEABFpCCFH63KHA/Photoroom_20260914_103442.png',
  heroImage: 'https://cdn.magicpatterns.com/uploads/vANKsy7HEdL1NwSV1uAfAj/aj.png',
}

export const contractorTypes: {
  tag: string
  title: string
  body: string
  icon: IconComponent
  verdict: string
}[] = [
  {
    tag: 'Type 1',
    title: 'The Unlicensed Vanisher',
    body: 'Works out of a truck, undercuts everyone by 40%, does mediocre work, and disappears when something cracks six months later.',
    icon: GhostContractorIcon,
    verdict: 'Zero recourse. Zero accountability.',
  },
  {
    tag: 'Type 2',
    title: 'The Big Corporate Chain',
    body: 'Looks professional. Has a website. But your job is a job number. Quotes take 3 weeks and communication runs through a phone tree.',
    icon: CorporateIcon,
    verdict: 'Premium prices for commodity work.',
  },
]

export const honestLocal = {
  tag: 'Type 3',
  title: 'The Honest Local — the one most owners never find',
  points: [
    'Licensed and insured, with proof on request',
    'Transparent pricing that holds from quote to final invoice',
    'Shows up when promised and answers the phone',
    'Stands behind the work, because the name on it is ours',
  ],
  icon: HandshakeIcon,
}

export const benefits: {
  title: string
  body: string
  icon: IconComponent
}[] = [
  {
    title: 'Licensed & Insured From Day One',
    body: "You're not gambling. You're working with professionals operating legally. If something goes wrong, you have real protection. Period.",
    icon: ShieldIcon,
  },
  {
    title: '20 Years of Real Experience',
    body: "Hundreds of pools plastered. We've seen every problem and every misstep — and we've learned how to prevent them.",
    icon: YearsIcon,
  },
  {
    title: 'Transparent, Honest Pricing',
    body: "No surprise fees. No upsells you didn't authorize. We give you a number and we stick to it.",
    icon: PriceTagIcon,
  },
  {
    title: 'Workmanship That Lasts',
    body: 'Meticulous prep. Premium materials. A finish that stays beautiful and durable. We stand behind it because we are proud of it.',
    icon: TrowelIcon,
  },
  {
    title: 'We Actually Show Up',
    body: "You won't wait weeks for a callback. You'll talk to a real person. We respect your time because we know how valuable it is.",
    icon: PhoneIcon,
  },
]

export const services: {
  name: string
  body: string
  icon: IconComponent
  image: string
  imageAlt: string
  highlights: string[]
}[] = [
  {
    name: 'Pool Plaster',
    body: 'Recolor, replaster, or full restoration. Meticulous chip-out and prep, premium material, and a finish that stays smooth and vibrant for years.',
    icon: WaveIcon,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ec934146-1ae8-4a35-bab2-cb20c79882db.jpg',
    imageAlt: 'Freshly replastered pool interior with a smooth surface and crisp waterline tile',
    highlights: ['Recolor & replaster', 'Full restoration', '3–4 week turnaround'],
  },
  {
    name: 'Complete Remodels',
    body: 'Reimagine the entire backyard — waterline to decking, spa to water feature — with one crew and one honest number from start to finish.',
    icon: RemodelIcon,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/0ecc68a5-7c2a-4790-b10e-6c747c5b7654.jpg',
    imageAlt: 'Remodeled backyard pool with travertine deck, raised spa, and landscaping at golden hour',
    highlights: ['Decking & hardscape', 'Spas & water features', 'One fixed quote'],
  },
  {
    name: 'Tile Work',
    body: 'Fresh, modern, or classic waterline tile set clean and straight, with grout lines that still look right ten summers from now.',
    icon: TileIcon,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/243f8349-68b5-44e1-bcfa-33c970f688af.jpg',
    imageAlt: 'Close-up of glossy blue mosaic waterline tile with water lapping against it',
    highlights: ['Waterline & step tile', 'Glass or ceramic', 'Precision grout lines'],
  },
  {
    name: 'Coping',
    body: 'Edge-to-edge perfection that frames the whole pool — cut, set, and sealed so the transition from deck to water reads as one clean line.',
    icon: CopingIcon,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/e1cb6422-141f-408a-9c56-0ec664a01bce.jpg',
    imageAlt: 'Clean cut stone coping along a pool edge next to a light travertine deck',
    highlights: ['Stone & travertine', 'Bullnose edges', 'Sealed and level'],
  },
  {
    name: 'Spray Deck Repair',
    body: 'Cracked, flaking, or faded spray deck (cool deck) patched, re-textured, and recolored so the surface around your pool stays cool underfoot and matches edge to edge.',
    icon: DeckIcon,
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/b9c76621-af68-4593-a8b3-ca33582d7bd7.jpg',
    imageAlt: 'Freshly resurfaced textured spray deck around a backyard pool with clean control joints',
    highlights: ['Crack & chip repair', 'Knockdown re-texture', 'Full recolor & seal'],
  },
]

export const confidencePoints: {
  title: string
  body: string
  icon: IconComponent
}[] = [
  {
    title: 'Licensed & Insured Since Day One',
    body: 'No shortcuts, no liability questions.',
    icon: ShieldIcon,
  },
  {
    title: '9 Out of 10 Customers Rehire Us',
    body: "That statistic isn't luck — it's consistency.",
    icon: StarIcon,
  },
  {
    title: '20-Year Track Record',
    body: "We're still here. Still doing the work. Still answering phones.",
    icon: YearsIcon,
  },
  {
    title: 'Family-Owned Accountability',
    body: 'We put our name on this. Our reputation is everything.',
    icon: FamilyIcon,
  },
  {
    title: 'Free Estimates, No Pressure',
    body: "We come out, assess, and quote honestly. You decide.",
    icon: PriceTagIcon,
  },
]

export const faqs: { question: string; answer: string }[] = [
  {
    question: 'How much does pool plaster actually cost?',
    answer:
      "Prices vary based on size, current condition, and the finish you want. That's exactly why we offer free estimates. We've done $3K jobs and $25K remodels — we'll tell you honestly what yours needs.",
  },
  {
    question: 'How long does the work take?',
    answer:
      "Plaster jobs typically take 3–4 weeks including curing time. Tile and remodels vary. You'll get an exact timeline in your free estimate.",
  },
  {
    question: 'What if something goes wrong after you finish?',
    answer:
      "We stand behind our work. We're licensed and insured, so you have actual recourse. Honestly, we rarely have problems because we do the prep and the materials right the first time.",
  },
  {
    question: 'Are you licensed?',
    answer:
      "Yes — fully licensed and insured since day one, and we can provide proof. That's non-negotiable for us.",
  },
  {
    question: 'Why should I pick you over another contractor?',
    answer:
      "Because we've been here 20 years, we answer our phones, we give honest pricing, and 9 out of 10 customers come back. We're not the flashiest — we're the most reliable.",
  },
  {
    question: 'Can you work with my schedule?',
    answer:
      "We'll do our best to accommodate. Message or call and we'll figure out timing that works for you.",
  },
]

export const transformationSteps: { step: string; title: string; body: string }[] = [
  {
    step: 'Week 1',
    title: 'Free estimate and honest scope',
    body: 'We come out, look at the real condition of the shell, and give you one number with nothing hidden behind it.',
  },
  {
    step: 'Weeks 1–2',
    title: 'Prep done properly',
    body: 'Chip-out, surface prep, tile and coping work. This is the step cheap contractors skip — and the reason their finishes fail.',
  },
  {
    step: 'Weeks 2–3',
    title: 'Plaster and finish',
    body: 'Premium material applied by the same crew, every day, until the surface is smooth and the color is exactly what you chose.',
  },
  {
    step: 'Weeks 3–4',
    title: 'Fill, cure, and hand back',
    body: 'Proper fill and curing, a final walkthrough, and a backyard you want people to see again.',
  },
]
